import { useEffect, useRef, useState } from "react";
import style from "./pillCategories.module.css";
import { IoIosSearch } from "react-icons/io";
import {
  GenresList,
  getPillCategorySearch,
  pillCategoriesApi,
  pillCategoriesUpdate,
  singlePillCategories,
} from "../../api/HttpApis";
import Button from "../../component/button/Button";
import InputText from "../../component/inputText/InputText";
import { showToast } from "../../utils/Toastify/Toastify";
import Chips from "../../component/chips/Chips";
import { FaRegImage } from "react-icons/fa";

const PillCategoriesModal = ({
  isOpen,
  onClose,
  onInviteSuccess,
  handleEditId,
  setHandleEditId,
  radioValue,
  setRadioValue,
  setShowGenres,
  showGenres,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    status: "",
    content_ids: "",
  });
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [editData, setEditData] = useState();
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [genresList, setGenresList] = useState();
  const [selectedGenreChips, setSelectedGenreChips] = useState([]);
  const timeoutRef = useRef();
  const resetForm = () => {
    setFormData({ name: "", status: "", content_ids: "" });
    setSelectedItems([]);
    setSearchQuery("");
    setEditData(null);
    setSearchResults([]);
    setShowDropdown(false);
    setHandleEditId(null);
    setShowSearch(false);
    setSelectedGenreChips([]);
    setRadioValue("");
  };
  const handleGenreChipToggle = (name, id) => {
    setSelectedGenreChips((prev) => {
      const exists = prev.find((item) => item.name === name);
      if (exists) {
        return prev.filter((item) => item.name !== name);
      } else {
        return [...prev, { name, id }];
      }
    });
  };

  useEffect(() => {
    const getGenresList = async () => {
      const response = await GenresList();
      setGenresList(response?.data?.data);
    };
    getGenresList();
  }, []);
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
      resetForm();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (!value.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        const response = await getPillCategorySearch(value);
        if (
          response?.data?.status === "success" &&
          Array.isArray(response.data?.data)
        ) {
          setSearchResults(response.data.data);
          setShowDropdown(true);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } catch (error) {
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 500);
  };

  const handleCheckboxChange = (item) => {
    setSelectedItems((prevSelected) => {
      const isAlreadySelected = prevSelected.includes(item.idforPillCategory);

      if (isAlreadySelected) {
        return prevSelected.filter((id) => id !== item.idforPillCategory);
      } else {
        return [...prevSelected, item.idforPillCategory];
      }
    });
  };

  useEffect(() => {
    const getSingleList = async () => {
      if (handleEditId) {
        const response = await singlePillCategories(handleEditId);
        const data = response?.data?.data;

        setEditData(data);

        setFormData({
          name: data?.name || "",
          status: data?.status || "active",
          content_ids: "",
        });

        const type = data?.type?.toLowerCase();
        setRadioValue(type);

        if (type === "manual") {
          setShowSearch(true);
          setShowGenres(false);
          setSelectedItems(data?.items?.map((item) => item.idforPillCategory));
          setSearchResults(data?.items || []);
        } else {
          setShowGenres(true);
          setShowSearch(false);
          const genreChips = data?.items?.map((item) => ({
            name: item.name,
            id: item.id,
          }));
          setSelectedGenreChips(genreChips);
        }

        if (data?.items?.length > 0) {
          const initialSelected = data.items.map(
            (item) => item.idforPillCategory
          );
          setSelectedItems(initialSelected);
        }
      } else if (isOpen) {
        setFormData({ name: "", status: "", content_ids: "" });
        setSelectedItems([]);
        setSearchQuery("");
        setEditData(null);
        setSearchResults([]);
        setShowDropdown(false);
      }
    };

    getSingleList();
  }, [handleEditId, isOpen]);

  const handleCategory = async (e) => {
    e.preventDefault();

    const dataToSend = {
      name: formData.name,
      status: "active",
      type: radioValue === "manual" ? "Manual" : "Genre",
      content_ids:
        selectedGenreChips.length > 0
          ? selectedGenreChips.map((item) => item.id)
          : selectedItems,
    };
    try {
      setLoading(true);
      if (editData) {
        const response = await pillCategoriesUpdate(handleEditId, dataToSend);
        showToast(response?.data?.message, "success");
        onInviteSuccess?.();
        onClose();
        resetForm();
      } else {
        const response = await pillCategoriesApi(dataToSend);
        showToast(response?.data?.message, "success");
        onInviteSuccess?.();
        onClose();
        resetForm();
      }
    } catch (error) {
      const response = error?.response?.data;
      if (response?.errors) {
        const fieldErrors = Object.values(response.errors).flat();
        if (fieldErrors.length > 0) {
          showToast(fieldErrors[0], "error");
        } else if (response.message) {
          showToast(response.message, "error");
        }
      } else if (response?.message) {
        showToast(response.message, "error");
      } else {
        showToast("An unexpected error occurred", "error");
      }
    } finally {
      setLoading(false);
    }
  };
  const handleRadio = (e) => {
    setRadioValue(e.target.value);
    if (e.target.value === "manual") {
      setShowSearch(true);
      setShowGenres(false);
      setSelectedItems([]);
      setSelectedGenreChips([]);
      setSearchQuery("");
      setSearchResults(editData?.items || []);
    } else {
      setShowGenres(true);
      setShowSearch(false);
      setSearchResults([]);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={style.overlay} onClick={handleOverlayClick}>
        <div className={style.modal}>
          <div className={style.headingText}>New Pill Category</div>
          <form onSubmit={handleCategory}>
            <div className={style.inputBox}>
              <div className={style.mainBox}>
                <div>
                  <InputText
                    name="name"
                    value={formData.name}
                    placeholder="Enter Name"
                    type="text"
                    variant="modal"
                    onChange={handleChange}
                  />
                </div>
                {showSearch ? (
                  <div className={style.searchContainer}>
                    <div className={style.searchWrapper}>
                      <IoIosSearch className={style.searchIcon} />
                      <InputText
                        name="search"
                        value={searchQuery}
                        placeholder="Search Content"
                        type="text"
                        variant="modal"
                        className={`${style.inputText1} ${style.withIcon}`}
                        onChange={(e) => handleSearchChange(e)}
                      />
                    </div>
                    {(showDropdown && searchQuery.length > 0) ||
                    handleEditId ? (
                      <div className={style.dropdown}>
                        {searchResults?.length > 0 ||
                        editData?.items?.length > 0 ? (
                          (searchResults?.length > 0
                            ? searchResults
                            : editData?.items
                          )?.map((item) => (
                            <label
                              key={item?.id}
                              className={style.dropdownItem}
                            >
                              <input
                                className={style.checkBoxStyle}
                                type="checkbox"
                                checked={selectedItems.includes(
                                  item.idforPillCategory
                                )}
                                onChange={() => handleCheckboxChange(item)}
                              />
                              <div>
                                {item?.portrait ? (
                                  <img
                                    src={item?.portrait}
                                    // alt={item?.name}
                                    className={style.avatar}
                                  />
                                ) : (
                                  <FaRegImage className={style.avatar} />
                                )}
                              </div>
                              <div className={style.nameText}>
                                <span>{item?.name}</span>
                                <span className={style.contentType}>
                                  {item?.content_type}
                                </span>
                              </div>
                            </label>
                          ))
                        ) : (
                          <div className={style.noResult}>No content found</div>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div>Content Selection Method</div>
              <div className={style.radioBtn}>
                <input
                  type="radio"
                  id="genres"
                  name="selection"
                  value="genre"
                  checked={radioValue === "genre"}
                  onChange={handleRadio}
                  disabled={handleEditId && radioValue === "manual"}
                />
                <label htmlFor="genres">Genres</label>

                <input
                  type="radio"
                  id="manual"
                  name="selection"
                  value="manual"
                  checked={radioValue === "manual"}
                  onChange={handleRadio}
                  disabled={handleEditId && radioValue === "genre"}
                />

                <label for="manual">Manual Selection</label>
                <br />
              </div>
            </div>
            {showGenres ? (
              <div style={{ marginTop: "20px" }}>
                <div className={style.chipInputBox}>
                  {selectedGenreChips?.map((item) => (
                    <div key={item.name} className={style.chipInInput}>
                      {item.name}
                      <span
                        className={style.remove}
                        onClick={() =>
                          handleGenreChipToggle(item.name, item.id)
                        }
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
                <div className={style.chipBox}>
                  {genresList?.map((item) => {
                    const isSelected = selectedGenreChips.some(
                      (chip) => chip.name === item.name
                    );

                    return (
                      <div
                        key={item.id}
                        style={{ display: "flex", cursor: "pointer" }}
                        onClick={() =>
                          handleGenreChipToggle(item.name, item.id)
                        }
                      >
                        <Chips
                          value={item.name}
                          type="selected"
                          className={`${style.chip} ${
                            isSelected ? style.selectedChip : ""
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div className={style.sendBtn}>
              <Button
                className={style.inviteText}
                type="submit"
                loading={loading}
                style={{ background: loading ? "none" : "#b87333" }}
              >
                {handleEditId ? "Update Category" : "Add Category"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default PillCategoriesModal;
